class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    this.loadRecommendations();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    // Heritage Olive: remember which lines were in the drawer so the new ones can animate in after the swap.
    const previousKeys = new Set(
      Array.from(this.querySelectorAll('.cart-item[data-key]')).map((item) => item.dataset.key)
    );

    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    this.markNewItems(previousKeys);
    this.pulseCartBubble();
    this.loadRecommendations();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  // Lines whose key was not in the drawer before get a class that fades them in (CSS runs it once the drawer is open).
  markNewItems(previousKeys) {
    this.querySelectorAll('.cart-item[data-key]').forEach((item) => {
      if (previousKeys.has(item.dataset.key)) return;
      item.classList.add('cart-item--new');
      // drop the class once the fade has run, so reopening the drawer does not replay it
      item.addEventListener('animationend', function onEnd(event) {
        if (event.target !== item) return;
        item.classList.remove('cart-item--new');
        item.removeEventListener('animationend', onEnd);
      });
    });
  }

  // The header's count bubble is replaced on every render, so the class goes on after the swap.
  pulseCartBubble() {
    const bubbles = document.querySelectorAll('#cart-icon-bubble .cart-count-bubble');
    bubbles.forEach((bubble) => {
      bubble.classList.remove('pulse');
      void bubble.offsetWidth;
      bubble.classList.add('pulse');
      setTimeout(() => bubble.classList.remove('pulse'), 600);
    });
  }

  loadRecommendations() {
    const panel = this.querySelector('[data-cart-recommendations]');
    if (!panel || panel.dataset.loaded === 'true') return;

    const grid = panel.querySelector('[data-cart-recommendations-grid]');
    if (!grid || !panel.dataset.url) return;

    const url = new URL(panel.dataset.url, window.location.origin);
    const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (previewTheme && !url.searchParams.has('preview_theme_id')) {
      url.searchParams.set('preview_theme_id', previewTheme);
    }

    fetch(url.toString())
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const items = Array.from(html.querySelectorAll('.heritage-reco__item')).slice(0, 2);
        if (!items.length) {
          panel.hidden = true;
          return;
        }
        grid.innerHTML = items.map((item) => item.outerHTML).join('');
        panel.dataset.loaded = 'true';
        panel.classList.add('is-loaded');
      })
      .catch((error) => {
        console.error('Cart recommendations failed', error);
        panel.hidden = true;
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
